import apiClient from './axios';
import { ApiResponse } from '../types';

export type ApprovalStatus =
  | 'pending'
  | 'executed'
  | 'failed'
  | 'rejected'
  | 'cancelled'
  | 'expired';

export interface ApprovalRequest {
  id: string;
  /** Key of the backend executor that runs on approval (e.g. shop.escrow.release). */
  actionKey: string;
  /** One-line human description of what approving will do. */
  summary: string;
  targetType: string | null;
  targetId: string | null;
  status: ApprovalStatus;
  requestedById: string | null;
  requestedByEmail: string | null;
  reviewedById: string | null;
  reviewedByEmail: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  /** Set when execution after approval threw — the action did NOT happen. */
  failureReason: string | null;
  /** Pending requests past this instant can no longer be approved. */
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

/** What a fund-moving endpoint returns instead of the action result when
 *  maker-checker is on (backend ADMIN_MAKER_CHECKER !== 'off'). */
export interface ApprovalRequired {
  approvalRequired: true;
  approval: ApprovalRequest;
}

/** Result of a maker-checker-wrapped action: executed directly (T) or
 *  parked as a pending approval a second admin must review. */
export type MaybeApproval<T> = T | ApprovalRequired;

export const isApprovalRequired = <T>(
  result: MaybeApproval<T>,
): result is ApprovalRequired =>
  typeof result === 'object' &&
  result !== null &&
  (result as ApprovalRequired).approvalRequired === true;

export interface ListApprovalsResult {
  items: ApprovalRequest[];
  total: number;
  page: number;
  limit: number;
}

export interface ListApprovalsParams {
  status?: ApprovalStatus;
  page?: number;
  limit?: number;
}

export const approvalsApi = {
  list: async (params: ListApprovalsParams = {}) => {
    const { data } = await apiClient.get<ApiResponse<ListApprovalsResult>>(
      '/admin/approvals',
      { params },
    );
    return data.data;
  },

  getById: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<ApprovalRequest>>(
      `/admin/approvals/${id}`,
    );
    return data.data;
  },

  /** Approve and execute server-side. 403 = the viewer is the maker
   *  (self-approval blocked); 400 = expired/already reviewed, or the
   *  approval was recorded but the action itself failed. */
  approve: async (id: string) => {
    const { data } = await apiClient.post<
      ApiResponse<{ approval: ApprovalRequest; result: unknown }>
    >(`/admin/approvals/${id}/approve`);
    return data.data;
  },

  /** Reject a pending request. The maker rejecting their own = cancelled. */
  reject: async (id: string, note?: string) => {
    const { data } = await apiClient.post<ApiResponse<ApprovalRequest>>(
      `/admin/approvals/${id}/reject`,
      note ? { note } : {},
    );
    return data.data;
  },
};
